import React, { useCallback, useEffect, useState } from "react";
import { CheckCircle2, FileKey2, KeyRound, RefreshCw, Save, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";


export const AdminFattura24 = () => {
    const [status, setStatus] = useState(null);
    const [apiKey, setApiKey] = useState("");
    const [busy, setBusy] = useState(false);
    const [syncing, setSyncing] = useState(false);

    const load = useCallback(async () => {
        try {
            const response = await api.get("/admin/fattura24/status");
            setStatus(response.data);
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Impossibile leggere la configurazione Fattura24");
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const save = async () => {
        if (!apiKey.trim()) return toast.error("Inserisci la API Key Fattura24");
        setBusy(true);
        try {
            await api.put("/admin/fattura24/config", { api_key: apiKey.trim(), enabled: true });
            setApiKey("");
            toast.success("Fattura24 collegato e API Key verificata");
            await load();
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Configurazione Fattura24 non riuscita");
        } finally { setBusy(false); }
    };

    const toggle = async (enabled) => {
        try {
            await api.patch("/admin/fattura24/enabled", { enabled });
            setStatus((current) => ({ ...current, enabled }));
            toast.success(enabled ? "Automazione Fattura24 attivata" : "Automazione Fattura24 sospesa");
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Aggiornamento non riuscito");
        }
    };

    const syncPending = async () => {
        setSyncing(true);
        try {
            const response = await api.post("/admin/fattura24/sync-pending");
            const result = response.data;
            toast.success(`${result.created} create · ${result.errors} errori · ${result.skipped} saltate`);
            await load();
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Sincronizzazione non riuscita");
        } finally { setSyncing(false); }
    };

    if (!status) return <div data-testid="fattura24-loading" className="text-sm text-ink-400">Caricamento configurazione…</div>;

    return (
        <section data-testid="fattura24-config-section" className="admin-neu-panel p-6 md:p-8 max-w-5xl">
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-5">
                <div>
                    <div className="flex items-center gap-3">
                        <span className="neu-icon-chip"><FileKey2 size={18} className="text-gold" /></span>
                        <div>
                            <h2 className="font-serif text-2xl text-ink-50">Configurazione Fattura24</h2>
                            <p className="text-sm text-ink-400 mt-1">Fatturazione elettronica ufficiale collegata ai pagamenti Stripe.</p>
                        </div>
                    </div>
                </div>
                <div data-testid="fattura24-connection-status" className={`fattura24-status ${status.configured && status.enabled ? "fattura24-status-ok" : ""}`}>
                    {status.configured ? <CheckCircle2 size={14} /> : <KeyRound size={14} />}
                    {status.configured ? (status.enabled ? "Collegato e attivo" : "Collegato · sospeso") : "Da configurare"}
                </div>
            </div>

            <div className="fattura24-key-panel mt-8">
                <label htmlFor="fattura24-api-key" className="label-eyebrow text-ink-300">API Key Fattura24</label>
                <div className="flex flex-col sm:flex-row gap-3 mt-3">
                    <input
                        id="fattura24-api-key"
                        data-testid="fattura24-api-key-input"
                        type="password"
                        autoComplete="new-password"
                        value={apiKey}
                        onChange={(event) => setApiKey(event.target.value)}
                        placeholder={status.key_hint || "Incolla la API Key"}
                        className="neu-input flex-1 px-4 py-3 text-sm"
                    />
                    <button data-testid="fattura24-save-button" type="button" onClick={save} disabled={busy} className="neu-btn neu-btn-active no-pill px-5 py-3 inline-flex items-center justify-center gap-2 disabled:opacity-50">
                        {busy ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
                        {busy ? "Verifica…" : "Verifica e salva"}
                    </button>
                </div>
                <p className="text-[11px] text-ink-500 mt-3">La chiave viene verificata con Fattura24, cifrata e non viene mai restituita al browser.</p>
            </div>

            {status.configured && (
                <div className="grid md:grid-cols-2 gap-4 mt-6">
                    <div className="fattura24-control-row">
                        <div>
                            <div className="text-sm text-ink-100 font-semibold">Automazione post-Stripe</div>
                            <div className="text-xs text-ink-500 mt-1">Crea il documento dopo pagamenti e rinnovi riusciti.</div>
                        </div>
                        <Switch data-testid="fattura24-enabled-switch" checked={status.enabled} onCheckedChange={toggle} />
                    </div>
                    <div className="fattura24-control-row">
                        <div>
                            <div className="text-sm text-ink-100 font-semibold">Documenti in attesa</div>
                            <div data-testid="fattura24-pending-count" className="text-xs text-ink-500 mt-1">{status.invoices?.pending || 0} fatture locali non ancora create.</div>
                        </div>
                        <button data-testid="fattura24-sync-pending-button" type="button" onClick={syncPending} disabled={syncing || !status.enabled} className="neu-btn no-pill px-3 py-2 inline-flex items-center gap-2 disabled:opacity-50">
                            <RefreshCw size={13} className={syncing ? "animate-spin" : ""} /> Sincronizza
                        </button>
                    </div>
                </div>
            )}

            <div className="mt-6 border border-amber-500/25 bg-amber-500/5 p-4 flex items-start gap-3">
                <ShieldCheck size={17} className="text-amber-300 mt-0.5 shrink-0" />
                <p className="text-xs leading-relaxed text-ink-300">
                    L&apos;API crea la fattura elettronica nell&apos;account Fattura24. L&apos;invio definitivo allo SDI viene eseguito dal Super-Admin nel pannello Fattura24 dopo il controllo fiscale.
                </p>
            </div>
        </section>
    );
};